import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, X, Clock } from 'lucide-react';
import { CATEGORIES } from '../mock/mock';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

function useNow() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  return now;
}

const NAV = [{ slug: '', label: 'Home' }, ...CATEGORIES, { slug: 'contact', label: 'Contact' }];

export default function Header() {
  const now = useNow();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);

  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Detroit' });
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Detroit' });

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/search?q=${encodeURIComponent(q.trim())}`); setMobileOpen(false); }
  };

  const linkPath = (slug) => (slug === '' ? '/' : slug === 'contact' ? '/contact' : `/category/${slug}`);
  const isActive = (slug) => {
    if (slug === '') return location.pathname === '/';
    if (slug === 'contact') return location.pathname === '/contact';
    return location.pathname === `/category/${slug}`;
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-[0_1px_0_rgba(0,0,0,0.06)]">
      {/* Masthead */}
      <div className="border-b border-[#e3ece7]">
        <div className="max-w-[1320px] mx-auto px-4 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 -ml-2 text-[#0f5132]" aria-label="Open menu">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] p-0">
                <div className="px-5 py-4 bg-[#0f5132] text-white flex items-center gap-2.5">
                  <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0"><img src="/tmb-logo.png" alt="TMB Global News" className="w-8 h-8 object-contain" /></span>
                  <span className="font-display font-bold text-lg">TMB Global News</span>
                </div>
                <nav className="flex flex-col py-2">
                  {NAV.map((n) => (
                    <Link key={n.label} to={linkPath(n.slug)} onClick={() => setMobileOpen(false)}
                      className={`px-5 py-3 text-[15px] font-medium border-b border-[#eef2f0] ${isActive(n.slug) ? 'text-[#157347] bg-[#eef8f2]' : 'text-[#14241d]'}`}>
                      {n.label}
                    </Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>

            <Link to="/" className="flex items-center gap-2.5">
              <img src="/tmb-logo.png" alt="TMB Global News logo" className="w-11 h-11 sm:w-12 sm:h-12 object-contain shrink-0" />
              <span className="leading-none">
                <span className="block font-display font-extrabold text-[22px] sm:text-[26px] tracking-tight text-[#0b3d26]">
                  TMB <span className="text-[#157347]">Global</span> News
                </span>
                <span className="hidden sm:block text-[11px] font-semibold uppercase tracking-[0.22em] text-[#5b6b63] mt-0.5">Michigan · Local &amp; Global</span>
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden md:flex flex-col items-end text-right">
              <span className="text-[12px] font-semibold text-[#14241d]">{dateStr}</span>
              <span className="inline-flex items-center gap-1 text-[11px] text-[#5b6b63]"><Clock className="w-3 h-3" />{timeStr} ET</span>
            </div>
            <form onSubmit={submit} className="relative">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search Michigan news"
                className="w-36 sm:w-56 pl-9 pr-3 py-2 text-sm rounded-full bg-[#f2f6f4] border border-transparent focus:border-[#2e9e6b] focus:bg-white outline-none transition-colors"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5b6b63]" />
            </form>
          </div>
        </div>
      </div>

      {/* Category nav */}
      <div className="bg-[#0f5132]">
        <div className="max-w-[1320px] mx-auto px-2 hidden lg:flex items-center">
          {NAV.map((n) => (
            <Link key={n.label} to={linkPath(n.slug)}
              className={`relative px-4 py-3 text-[14px] font-semibold uppercase tracking-wide font-cond transition-colors ${isActive(n.slug) ? 'text-white bg-[#157347]' : 'text-[#cdeddb] hover:text-white hover:bg-white/10'}`}>
              {n.label}
            </Link>
          ))}
          <span className="ml-auto pr-4 py-3 text-[12px] font-medium text-[#8be0b0] inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#4ade80] breaking-dot" /> Live updates twice daily
          </span>
        </div>
        {/* mobile category strip */}
        <div className="lg:hidden overflow-x-auto">
          <div className="flex items-center px-2 min-w-max">
            {NAV.filter((n) => n.slug !== 'contact').map((n) => (
              <Link key={n.label} to={linkPath(n.slug)}
                className={`px-3.5 py-2.5 text-[13px] font-semibold uppercase tracking-wide font-cond whitespace-nowrap ${isActive(n.slug) ? 'text-white' : 'text-[#cdeddb]'}`}>
                {n.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
